import * as React from 'react';
import { mergeDeepRight } from 'ramda';
import * as ChartLib from 'chart.js';

import { getGradientEstimatedPrice, getGradientOfferPrice } from './getChartLineGradient';
import { BASE_CONFIG, mergeDataWithBaseDataset, yAxisTicksCb, tooltipLabelCb } from './chartConfiguration';
import { IChartConfigOptions } from '../../../types';
import { getPriceUnit } from '../../CostEstimateContainer/utils/getPriceUnit';
import { RED_COLOR, POINT_RADIUS, POINT_HOVER_RADIUS } from '../../../../../constants/valuation/chart';

export const usePrepareChartConfig = (ctx: CanvasRenderingContext2D | null, options: IChartConfigOptions) => {
  const priceUnit = getPriceUnit(options.dealType);

  return React.useMemo(() => {
    if (!ctx) {
      return null;
    }

    const gradientEstimatedPrice = getGradientEstimatedPrice(ctx);
    const gradientOfferPrice = getGradientOfferPrice(ctx);
    const gradients = { gradientEstimatedPrice, gradientOfferPrice };

    const estimatedPriceDataset = mergeDataWithBaseDataset(1, {
      data: options.valuationPrices,
      label: `Рыночная ${options.dealType === 'sale' ? 'цена' : 'ставка'}`,
      backgroundColor: gradients.gradientEstimatedPrice,
    });

    const isOnePoint = options.historyPrices?.filter(Boolean).length === 1;
    const offerPriceDataset = mergeDataWithBaseDataset(0, {
      data: options.historyPrices,
      label: 'В объявлении',
      backgroundColor: gradients.gradientOfferPrice,
      // Если в данных всего 1 точка - рисуем ее сразу видимой, без ховера
      pointBackgroundColor: isOnePoint ? RED_COLOR : undefined,
      pointRadius: isOnePoint ? POINT_HOVER_RADIUS : POINT_RADIUS,
    });
    const datasets = [estimatedPriceDataset, offerPriceDataset];

    return mergeDeepRight(BASE_CONFIG, {
      data: {
        labels: options.datesLabels,
        datasets,
      },
      options: {
        scales: {
          y: {
            min: options.minChartPrice,
            max: options.maxChartPrice,
            ticks: {
              stepSize: options.priceLabelStep,
              callback: yAxisTicksCb(priceUnit),
            },
          },
        },
        onHover: options.onHover,
        tooltips: {
          callbacks: {
            label: tooltipLabelCb(priceUnit),
          },
        },
      },
    }) as ChartLib.ChartConfiguration;
  }, [ctx]);
};
